import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/react-hooks';
import { withRouter } from 'react-router-dom';
import Button from './Button';
import colors from '../styles/colors';
import { REGISTER_USER } from '../graphql/queries';
import { getErrorMessage } from '../util/ErrorUtil';
import Error from './Error';
import Input, { PhoneNumberInput } from './Input';
import { Flex } from './Flex';
import { FadeIn } from './Animation';
import { media } from '../util/MediaQuery';

const Content = styled.div`
  align-items: center;
  justify-content: center;
`;
const Form = styled.form`
  align-items: center;
  width: 100%;
  align-items: center;
  justify-content: center;
  background: ${colors.white};

  border-radius: 5px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  margin: auto;
  overflow-y: auto;
  ${media.desktop`
  &::-webkit-scrollbar {
      width: 7px;
      height: 7px;
  }
  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: grey;
    border-radius: 10px;
  }
    padding: 30px;
    width: 400px;
  `};

  ${media.mobile`
    height: 100vh;
    width: 100vw;
    padding: 10px;
  `};
  ${media.desktop`
    min-height: 85vh !important;
    max-height: 85vh !important; 
  `};
  ${media.tablet`
    min-height: 100vh;
    max-height: 100vh; 
  `};
  display: grid;
  width: 100%;
  grid-template-columns: 1fr;
`;

const InnerContainer = styled.div`
  align-items: center;
  justify-content: center;
`;

const TopText = styled.div`
  font-size: 2rem;
  text-align: center;
  padding-top: 10px;
`;

const BottomText = styled.div`
  font-size: 1.2rem;
  color: ${(props) => props.color || `${colors.grey3}`};
  cursor: ${(props) => (props.onClick ? 'pointer' : null)};
`;

const NameContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const TOS = styled.div`
  color: ${colors.grey3};
  padding: 25px 0px 15px 0px;
  text-align: center;
`;

const TOSLink = styled.a`
  color: ${colors.seaGreen};
`;

const Spacer = styled.div`
  width: 15px;
`;

function Register({ history }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [countdown, setCountdown] = useState(null);

  const [register, { loading }] = useMutation(REGISTER_USER, {
    variables: {
      email,
      firstName,
      lastName,
      phoneNumber,
      password,
    },
    onError(error) {
      setErrorMsg(getErrorMessage(error));
      setCountdown(5);
    },
    onCompleted(data) {
      localStorage.setItem('CHECK_THIS_OUT_AUTH_TOKEN', data.register.token);
      history.push('/onboarding');
    },
  });

  useEffect(() => {
    if (countdown !== null && countdown >= 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            history.push('/login');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [countdown, history]);
  return (
    <Content>
      <FadeIn duration={0.6}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            register();
          }}
        >
          <InnerContainer>
            <TopText>Let's make you an account.</TopText>
            <NameContainer>
              <Input
                type="text"
                header="FIRST NAME"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                width="100%"
              />
              <Spacer />
              <Input
                type="text"
                header="LAST NAME"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                width="100%"
              />
            </NameContainer>
            <Input
              type="email"
              header="EMAIL"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              width="100%"
            />
            <PhoneNumberInput
              type="tel"
              header="PHONE NUMBER"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              required
              width="100%"
            />
            <Input
              type="password"
              header="PASSWORD"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              width="100%"
            />
            <TOS>
              By registering, you agree to Check This Out's&nbsp;
              <TOSLink
                href="https://intercom.help/check-this-out/en/articles/4339634-check-this-out-terms-of-use-and-privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use and Privacy Policy
              </TOSLink>
            </TOS>
            <Button
              margin="0px 0px 30px 0px"
              text="REGISTER"
              type="submit"
              loading={loading}
              width="100%"
            />
            <Flex justify="center" padding="10px 0px">
              <BottomText>ALREADY HAVE AN ACCOUNT?</BottomText>
              <BottomText
                color={colors.seaGreen}
                onClick={() => history.push('/login')}
              >
                &nbsp;&nbsp;&nbsp;SIGN IN
              </BottomText>
            </Flex>
            {errorMsg && (
              <>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '2px',
                    position: 'relative',
                  }}
                >
                  <Error error={errorMsg} position="relative" />
                  <div
                    style={{
                      color: colors.seaGreen,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontWeight: 500,
                      position: 'absolute',
                      right: '50px',
                      top: '23px',
                      transform: 'translateY(-50%)',
                    }}
                    onClick={() => history.push('/login')}
                  >
                    Please login
                  </div>
                </div>

                {countdown !== null && countdown >= 0 && (
                  <BottomText style={{ marginTop: '10px', color: '#999', paddingLeft: '20px' }}>
                    You will be automatically redirected to the login screen in{' '}
                    <strong>{countdown}</strong> seconds.
                  </BottomText>
                )}
              </>
            )}
          </InnerContainer>
        </Form>
      </FadeIn>
    </Content>
  );
}

export default withRouter(Register);
